import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Heart, LogOut, Trash2, TrendingUp } from "lucide-react";
import { api, errMsg, useApi } from "../lib/api";
import { useBi, useCustomer } from "../lib/customer";
import { useI18n } from "../lib/i18n";
import { useFavorites } from "../lib/store";
import { fmtDual } from "../lib/format";
import { PageHero, Loading } from "../components/Common";
import Seo from "../components/Seo";

function AuthForms() {
  const b = useBi();
  const { login, register } = useCustomer();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "", phone: "", is_investor: false });
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(f.email, f.password); else await register({ ...f, phone: f.phone || null });
      toast.success(b("Welcome!", "أهلاً بك!"));
      if (params.get("next")) nav(params.get("next"));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const inp = (k, label, type = "text", extra = {}) => (
    <label className="block"><span className="mb-1.5 block text-[11px] uppercase tracking-wider text-fg/55">{label}</span>
      <input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`account-${k}`} className="field" {...extra} /></label>
  );
  return (
    <div className="glass mx-auto max-w-md rounded-sm p-8" data-testid="account-auth">
      <div className="mb-6 flex gap-2">
        {["login", "register"].map((m) => (<button key={m} type="button" onClick={() => setMode(m)} data-testid={`account-tab-${m}`} className={`chip ${mode === m ? "chip-on" : ""}`}>{m === "login" ? b("Sign in", "تسجيل الدخول") : b("Create account", "إنشاء حساب")}</button>))}
      </div>
      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && inp("name", b("Full name", "الاسم الكامل"), "text", { required: true, minLength: 2 })}
        {inp("email", b("Email", "البريد الإلكتروني"), "email", { required: true, dir: "ltr", autoComplete: "email" })}
        {inp("password", b("Password", "كلمة المرور"), "password", { required: true, minLength: mode === "register" ? 8 : 1, dir: "ltr", autoComplete: mode === "login" ? "current-password" : "new-password" })}
        {mode === "register" && inp("phone", b("Mobile (optional)", "الجوال (اختياري)"), "tel", { dir: "ltr" })}
        {mode === "register" && (
          <label className="flex items-center gap-2 text-sm text-fg/75"><input type="checkbox" className="h-4 w-4 accent-cta" checked={f.is_investor} onChange={(e) => setF({ ...f, is_investor: e.target.checked })} data-testid="account-is_investor" />{b("I'm an investor", "أنا مستثمر")}</label>
        )}
        <button type="submit" disabled={busy} data-testid="account-submit" className="btn-cta w-full">{busy ? "…" : mode === "login" ? b("Sign in", "دخول") : b("Create account", "إنشاء الحساب")}</button>
      </form>
      <p className="mt-4 text-xs text-fg/45">{b("Customer accounts are separate from staff accounts.", "حسابات العملاء منفصلة عن حسابات الموظفين.")}</p>
    </div>
  );
}

function SavedSearches() {
  const b = useBi();
  const [tick, setTick] = useState(0);
  const { data } = useApi(`/customer/saved-searches?t=${tick}`);
  const del = async (id) => { await api.delete(`/customer/saved-searches/${id}`); setTick((x) => x + 1); };
  return (
    <section className="glass rounded-sm p-6" data-testid="saved-searches">
      <h2 className="h-display mb-4 flex items-center gap-2 text-2xl"><Bookmark className="h-5 w-5 text-cta" />{b("Saved searches", "عمليات البحث المحفوظة")}</h2>
      {!data?.length ? <p className="text-sm text-fg/55">{b("No saved searches yet — use “Save this search” on the listings page.", "لا توجد عمليات بحث محفوظة — استخدم «احفظ هذا البحث» في صفحة العقارات.")}</p> : (
        <ul className="space-y-2">{data.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-3 border-b border-fg/5 pb-2" data-testid={`saved-search-${s.id}`}>
            <Link to={`${s.path}?${s.query}`} className="text-sm text-fg hover:text-cta">{s.name}<span className="ms-2 text-xs text-fg/40" dir="ltr">{s.query}</span></Link>
            <button type="button" onClick={() => del(s.id)} aria-label={b("Delete", "حذف")} data-testid={`saved-search-delete-${s.id}`}><Trash2 className="h-4 w-4 text-fg/50 hover:text-flame" /></button>
          </li>))}</ul>
      )}
    </section>
  );
}

export default function Account() {
  const b = useBi();
  const { customer, logout } = useCustomer();
  const { favs } = useFavorites();
  return (
    <div data-testid="account-page">
      <Seo title={b("My account", "حسابي")} />
      <PageHero eyebrow={b("Account", "الحساب")} title={customer ? b(`Welcome, ${customer.name}`, `أهلاً، ${customer.name}`) : b("My account", "حسابي")} sub={b("Save searches and sync favorites across devices.", "احفظ عمليات البحث وزامن المفضلة عبر أجهزتك.")} />
      <div className="container-lux py-14">
        {customer === undefined ? <Loading /> : !customer ? <AuthForms /> : (
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <SavedSearches />
            <aside className="space-y-3">
              <p className="text-sm text-fg/60" dir="ltr" data-testid="account-email">{customer.email}</p>
              <Link to="/favorites" className="btn-ghost w-full" data-testid="account-favorites-link"><Heart className="h-4 w-4" />{b(`Favorites (${favs.length})`, `المفضلة (${favs.length})`)}</Link>
              <Link to="/investor-portal" className="btn-ghost w-full" data-testid="account-investor-link"><TrendingUp className="h-4 w-4" />{b("Investor portal", "بوابة المستثمر")}</Link>
              <button type="button" onClick={logout} className="btn-ghost w-full" data-testid="account-logout"><LogOut className="h-4 w-4" />{b("Sign out", "تسجيل الخروج")}</button>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

export function InvestorPortal() {
  const b = useBi();
  const { lang } = useI18n();
  const { customer } = useCustomer();
  const { data } = useApi(customer ? "/customer/inquiries" : null);
  return (
    <div data-testid="investor-portal-page">
      <Seo title={b("Investor portal", "بوابة المستثمر")} />
      <PageHero eyebrow={b("Investors", "المستثمرون")} title={b("Investor portal", "بوابة المستثمر")} sub={b("Track your inquiries and their status with our team.", "تابع استفساراتك وحالتها لدى فريقنا.")} />
      <div className="container-lux py-14">
        {customer === undefined ? <Loading /> : !customer ? (
          <div className="glass mx-auto max-w-md rounded-sm p-8 text-center"><p className="mb-4 text-fg/70">{b("Sign in to view your inquiry history.", "سجّل الدخول لعرض سجل استفساراتك.")}</p>
            <Link to="/account?next=/investor-portal" className="btn-cta" data-testid="investor-signin-link">{b("Sign in / Register", "تسجيل الدخول / إنشاء حساب")}</Link></div>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-3"><Link to="/investment#investor-form" className="btn-cta" data-testid="investor-new-inquiry">{b("New investor inquiry", "استفسار استثماري جديد")}</Link><Link to="/land-investment" className="btn-ghost">{b("Investment listings", "فرص الاستثمار")}</Link></div>
            <div className="overflow-x-auto rounded-sm border border-fg/10" data-testid="investor-inquiries">
              <table className="w-full text-sm"><thead className="bg-surface text-[11px] uppercase text-fg/50"><tr>{[b("Date", "التاريخ"), b("Type", "النوع"), b("Regarding", "بخصوص"), b("Status", "الحالة")].map((h) => <th key={h} className="p-3 text-start">{h}</th>)}</tr></thead>
                <tbody>{(data || []).map((l) => (<tr key={l.id} className="border-t border-fg/5"><td className="p-3 text-xs">{fmtDual(l.created_at, lang)}</td><td className="p-3">{l.type}</td><td className="p-3 text-xs">{l.property_slug || l.project_slug || l.source}</td><td className="p-3"><span className="chip !py-0.5">{l.status}</span></td></tr>))}</tbody></table>
              {data && !data.length && <p className="p-6 text-center text-sm text-fg/55">{b("No inquiries yet. Inquiries you submit while signed in (or with this email) appear here.", "لا توجد استفسارات بعد. تظهر هنا الاستفسارات المرسلة أثناء تسجيل الدخول أو بنفس البريد.")}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function SaveSearch() {
  const b = useBi();
  const { customer } = useCustomer();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    const q = new URLSearchParams(params); ["page", "view", "nl", "nlq"].forEach((k) => q.delete(k));
    try { await api.post("/customer/saved-searches", { name: name || b("My search", "بحثي"), query: q.toString(), path: window.location.pathname }); toast.success(b("Search saved to your account", "تم حفظ البحث في حسابك")); setOpen(false); setName(""); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!customer) return <button type="button" className="chip" data-testid="save-search-button" onClick={() => { toast(b("Sign in to save searches", "سجّل الدخول لحفظ البحث")); nav(`/account?next=${encodeURIComponent(window.location.pathname + window.location.search)}`); }}><Bookmark className="h-3.5 w-3.5" />{b("Save this search", "احفظ هذا البحث")}</button>;
  return open ? (
    <form onSubmit={save} className="flex gap-2" data-testid="save-search-form"><input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={b("Name this search", "اسم البحث")} className="field !w-44 !py-2" data-testid="save-search-name" /><button type="submit" className="btn-cta !px-4 !py-2" data-testid="save-search-submit">{b("Save", "حفظ")}</button></form>
  ) : <button type="button" className="chip" data-testid="save-search-button" onClick={() => setOpen(true)}><Bookmark className="h-3.5 w-3.5" />{b("Save this search", "احفظ هذا البحث")}</button>;
}
